import React, {useState, useEffect} from "react";
import {
    Typography,
    Grid,
    makeStyles,
    Container,
    Card,
    Button,
    useTheme,
    useMediaQuery,
    Box, CircularProgress, Checkbox
} from "@material-ui/core";
import {RootState, useAppDispatch} from "../../redux/store";
import {useSelector} from "react-redux";
import {useSnackbar} from "notistack";
import API from "../../helpers/apiHelpers";
import Navigation from "../../Components/Navigation";
import {getUserProfile} from "../../redux/userProfile/userProfile.actions";
import CreditCardItem from "./CreditCardItem";
import {useHistory} from "react-router-dom";
import {
    setAllCards,
    setIsChangeCreditCard,
    setSelectedCard,
    setPaymentType
} from "../../redux/credits/credits.reducers";
import {ReactComponent as CheckboxBorder} from "../../Images/checkboxBorder.svg";
import CheckboxChecked from "../../Components/CheckboxChecked";
import {CheckBox} from "@material-ui/icons";

const useStyle = makeStyles(() => ({
    cardRoot: {
        width: "100%",
        padding: 40,
        marginBottom: 32,
    },
    containerRoot: {
        paddingTop: 100,
        marginBottom: 55,
        zIndex: 1,
    },
    body: {
        paddingTop: 20,
    },
    spacing: {
        paddingTop: 20,
    },
    spacingImage: {
        paddingTop: 20,
        display: "flex"
    },
    cardsContainer: {},
    emptyState: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    cardWrapper: {
        paddingTop: 30,
        borderTop: "1px solid #273878",
    },
    labelName: {
        color: "#273878",
        fontFamily: "NotoSerifDisplay",
        fontStyle: "normal",
        fontWeight: "bold",
        fontSize: "18px",
        marginBottom: 20,
        textAlign: "center"
    },
    cardInputWrapper: {
        height: 140,
        display: "flex",
        justifyContent: "space-between",
    }
}));

const PaymentInformation = () => {
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const user = useSelector((state: RootState) => state?.user);
    const isChangeCreditCard = useSelector((state: RootState) => state?.credits.isChangeCreditCard);
    const paymentType = useSelector((state: RootState) => state?.credits.paymentType);
    const cards = useSelector((state: RootState) => state?.credits.allCards);
    const [newCard, setNewCard] = useState([]);
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCustomer, setIsCustomer] = useState(null);
    const {enqueueSnackbar} = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const checkPaymentInfo = "Something went wrong with your card. Please make sure that you are using one of the following card types: Visa, Mastercard, American Express, Discover, Diners Club, JCB";
    const selectedCard = useSelector((state: RootState) => state?.credits.selectedCard);

    useEffect(() => {
        if (copied) {
            enqueueSnackbar("Copied!", {
                autoHideDuration: 4000
            });
            setCopied(false);
        }
    }, [copied]);

    async function initializeCard(payments) {
        const card = await payments.card();
        await card.attach("#card-container");
        return card;
    }

    useEffect(() => {
        return () => {
            dispatch(setIsChangeCreditCard(false));
        };
    }, []);

    async function tokenize(paymentMethod) {
        const tokenResult = await paymentMethod.tokenize();
        if (tokenResult.status === "OK") {
            return tokenResult;

        } else {
            let errorMessage = `Tokenization failed-status: ${tokenResult.status}`;
            if (tokenResult.errors) {
                errorMessage += ` and errors: ${JSON.stringify(
                    tokenResult.errors
                )}`;
            }
            throw new Error(errorMessage);
        }
    }

    const addNewCard = async (event, paymentMethod) => {
        event.preventDefault();
        let cardIdInfo = null;
        let nonce;
        let card;
        try {
            const res = await tokenize(paymentMethod);
            nonce = res?.token;
            const {brand, expMonth, expYear, last4} = res?.details?.card;
            card = {
                card_brand: brand,
                last_4: last4,
                exp_month: expMonth,
                exp_year: expYear,
            };
        } catch (e) {
            console.error(e.message);
        }
        let profile;
        if (user["userId"]) {
            profile = await dispatch(getUserProfile({userId: user["userId"]}));
        }
        let data = {
            "source_id": nonce,
            card,
        };
        try {
            const newCard = await API.createCreditCard(data);
            if (!!newCard?.id) {
                cardIdInfo = newCard.id;
                const cardsInfo = await API.getCreditCards();
                dispatch(setSelectedCard(cardsInfo.find(i => i.id === cardIdInfo) || cardsInfo[0]));
                dispatch(setAllCards(cardsInfo));
                history.push(`/invoice?type=${paymentType}`);
                dispatch(setIsChangeCreditCard(false));
                dispatch(setPaymentType(""));
            }
        } catch (e) {
            const message = e.message === "Card already registered" ? "This card is already in use" : checkPaymentInfo;
            enqueueSnackbar(message, {variant: "error", autoHideDuration: 4000});
        }
    };

    useEffect(() => {
        (async () => {
            if (!!isChangeCreditCard && !!cards.length && !document.querySelector(".sq-card-iframe-container")) {
                if (!window.Square) {
                    throw new Error("Square.js failed to load properly");
                }
                const payments = window.Square.payments(process.env.REACT_APP_SQ_APPLICATION_ID);
                try {
                    let info;
                    info = await initializeCard(payments);
                    setNewCard(info);
                } catch (e) {
                    console.error("Initializing Card failed", e);
                    return;
                }
                let nextDoorPixelScript = document.createElement("script");
                let content = document.createTextNode("ndp('track','CONVERSION');");
                nextDoorPixelScript.append(content);
                document.getElementsByTagName("head")[0].append(nextDoorPixelScript);
            }
        })();
    }, [cards]);

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const customer = await API.getCustomers();
                setIsCustomer(customer);
                const profileCards = await API.getCreditCards();
                dispatch(setAllCards(profileCards));
                setIsLoading(false);
            } catch (e) {
                setIsCustomer(false);
            }
        })();
    }, []);

    useEffect(()=>{
        (async () => {
            try {
                if(isCustomer === false){
                    setIsLoading(true);
                    let  profile;
                    if(user?.userId) {
                        profile = await dispatch(getUserProfile({userId: user.userId}));
                    }
                    let body = {
                        "givenName": profile[0].firstName || '',
                        "familyName": profile[0].lastName || '',
                        "companyName": "thevideovets.com",
                        "emailAddress": user.email || '',
                        "phoneNumber": profile[0].phoneNumber || ''
                    }
                    const data = await API.createCustomer(body);
                }
                const cardsInfo = await API.getCreditCards();
                dispatch(setAllCards(cardsInfo))
                setIsLoading(false);
            } catch {
                setIsLoading(false);
            }
        })()
    }, [isCustomer])

    const getCreditCardsInfo = async () => {
        const profileCards = await API.getCreditCards();
        dispatch(setAllCards(profileCards));
        setIsLoading(false);
    };

    const deleteCreditCard = async (id) => {
        const res = await API.deleteCreditCards(id);
        let data = cards.filter(i => i.id !== id);
        dispatch(setAllCards(data));
        const newSelected = data.find(({isDefault}) => isDefault === true);
        if (!newSelected && !data.length) {
            dispatch(setSelectedCard(null));
        } else if (!newSelected) {
            dispatch(setSelectedCard(cards[0]));
            await API.updateCreditCard(cards[0].id, !cards[0].isDefault);
        } else dispatch(setSelectedCard(newSelected));
    };

    useEffect(() => {
        (async () => {
            if (cards?.length && !selectedCard) {
                const item = cards.find(({isDefault}) => isDefault) || cards[0];
                const {id, isDefault} = item;
                dispatch(setSelectedCard({...item, isDefault: !isDefault}));
                await API.updateCreditCard(id, !isDefault);
                await getCreditCardsInfo();
            }
        })();
    }, [cards, selectedCard]);

    const toggleDefault = async (item, idx) => {
        const {isDefault} = item;
        const newItem = {...item, isDefault: !isDefault};
        if (isDefault) {
            const item = cards[0];
            const {id, isDefault} = item;
            dispatch(setSelectedCard({...item, isDefault: !isDefault}));
            await API.updateCreditCard(id, !isDefault);
        } else {
            dispatch(setSelectedCard(newItem));
            const {id, isDefault} = newItem;
            await API.updateCreditCard(id, isDefault);
        }
        await getCreditCardsInfo();
    };

    return (
        <Container maxWidth="md">
            <Box marginTop="115px" marginBottom="75px">
                <Grid container>
                    {!isMobile &&
                    <Grid item md={3} xs={12}>
                        <Navigation/>
                    </Grid>
                    }
                    <Grid item md={9} xs={12}>
                        <Card className={classes.cardRoot}>
                            <Container className={classes.body} disableGutters={true}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h2" style={{fontSize: 32, paddingBottom: 25}}>
                                            Stored Payment Information
                                        </Typography>
                                    </Grid>
                                    {isLoading ?
                                        <Box width="100%" height="300px" display="flex" justifyContent="center"
                                             alignItems="center">
                                            <CircularProgress/>
                                        </Box> :
                                        <Grid item xs={12} className={classes.cardsContainer}> {
                                            !!cards.length ? <>{cards.map((item, idx) => (
                                                    !isChangeCreditCard
                                                        ?
                                                        <Grid container
                                                              alignItems="center">
                                                            <Grid item xs={2}>
                                                                <Checkbox
                                                                    onClick={() => {
                                                                        toggleDefault(item, idx);
                                                                    }}
                                                                    checked={selectedCard?.id === item.id}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={10}>
                                                                <CreditCardItem
                                                                    item={item}
                                                                    deleteCreditCard={deleteCreditCard}
                                                                    key={item.id}
                                                                    isChangeCreditCard={isChangeCreditCard}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        :
                                                        <CreditCardItem
                                                            item={item}
                                                            deleteCreditCard={deleteCreditCard}
                                                            key={item.id}
                                                            isChangeCreditCard={isChangeCreditCard}
                                                        />

                                                ))}
                                                </>
                                                :
                                                !isChangeCreditCard
                                                &&
                                                (<Grid className={classes.emptyState}>
                                                    <Grid item md={12}>
                                                        <Typography className={classes.labelName}>
                                                            There are no stored payment methods. To add a stored payment
                                                            method, please request an appointment.
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item md={12}>
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => history.push("/schedule")}
                                                        >
                                                            Request Appointment & Store Payment
                                                        </Button>
                                                    </Grid>
                                                </Grid>)
                                        }
                                            {!!isChangeCreditCard &&
                                            <Grid className={classes.cardWrapper}>
                                                <Typography className={classes.labelName} style={{textAlign: "start"}}>
                                                    Add a new payment method
                                                </Typography>
                                                <Grid className={classes.cardInputWrapper} md={12}>
                                                    <form id="payment-form">
                                                        <div id="card-container"/>
                                                    </form>
                                                    <Grid item md={3}>
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            style={{minWidth: 80}}
                                                            onClick={(event) => addNewCard(event, newCard)}
                                                            color="primary">
                                                            Add
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            }
                                        </Grid>}
                                </Grid>
                            </Container>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default PaymentInformation;
